import React, { useState, useEffect } from "react";
import LoadingSpinner from "../../components/LoadingSpinner";
import ErrorMessage from "../../components/ErrorMessage";
import { useLanguage } from "../../contexts/LanguageContext";
import type { AnalyticsFilters } from "../../types/analytics";
import { fetchAnalyticsTasks } from "./analyticsApi";
import type { AnalyticsTaskRow } from "../../types/analytics";

interface TasksTabProps {
  filters: AnalyticsFilters;
}

const TasksTab: React.FC<TasksTabProps> = ({ filters }) => {
  const { t } = useLanguage();
  const [rows, setRows] = useState<AnalyticsTaskRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchAnalyticsTasks(filters)
      .then((res) => {
        if (!cancelled) setRows(res.rows);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || t("analytics.error"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [filters.from, filters.to, filters.task, filters.operatorId, t]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <LoadingSpinner />
      </div>
    );
  }
  if (error) {
    return <ErrorMessage message={error} onRetry={() => setError(null)} />;
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{t("analytics.task")}</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{t("analytics.leadsSent")}</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{t("analytics.attempts")}</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{t("analytics.reached")}</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{t("analytics.ordersCreated")}</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{t("analytics.reachRate")}</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{t("analytics.conversionRate")}</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                  {t("analytics.noData")}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.callCenterTask} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">{row.callCenterTask}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{row.leadsSent}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{row.attempts}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{row.reached}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{row.ordersCreated}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{row.reachRate}%</td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{row.conversionRate}%</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TasksTab;
